import { useState, useCallback } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { useAssetsStore } from '@/stores/assetsStore';
import { useInternetConnectionStore } from '@/stores/internetConnectionStore';
import { useDMZConfigStore } from '@/stores/dmzConfigStore';
import { useSiteStore } from '@/stores/siteStore';
import { useZoneStore } from '@/stores/zoneStore';
import { useSystemStore } from '@/stores/systemStore';
import { useCloudServiceStore } from '@/stores/cloudServiceStore';
import { useRisks, useTasks, useEvidence } from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';
import { useRelatedEntities } from '@/hooks/useRelatedEntities';
import { RiskDialog } from './RiskDialog';
import { TaskDialog } from './TaskDialog';
import { EvidenceDialog } from './EvidenceDialog';
import { NodeEditDialog } from './NodeEditDialog';
import { InternetEditDialog } from './InternetEditDialog';
import { DMZEditDialog } from './DMZEditDialog';
import { EndpointFormDialog } from './EndpointFormDialog';
import { SiteFormDialog } from '../sites/SiteFormDialog';
import { ZoneFormDialog } from '../sites/ZoneFormDialog';
import { SystemFormDialog } from '../systems/SystemFormDialog';
import { CloudServiceFormDialog } from '../cloudServices/CloudServiceFormDialog';
import { CloudResourceFormDialog } from '../cloudResources/CloudResourceFormDialog';
import { LinkManager } from '@/components/shared/LinkManager';
import { STANDARD_LINKABLE_TYPES } from '@/components/shared/EntityLinkSelector';
import { EntityType } from '@/types/link';
import { 
  Plus,
  CheckCircle2, 
  Shield,
  Trash2,
  Edit,
  Globe
} from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { toast } from 'sonner';
import type { Task, TaskStatus } from '@/hooks/queries/useTasks';
import type { RiskSeverity, TaskRecord, TaskCadence } from '@/types/assets';

dayjs.extend(relativeTime);

interface InspectorDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nodes: any[]; // React Flow nodes passed from parent
  onUpdateNodeData?: (nodeId: string, data: Record<string, any>) => void;
}

export function InspectorDrawer({ open, onOpenChange, nodes, onUpdateNodeData }: InspectorDrawerProps) {
  const { 
    selectedNodeId, 
    deleteNode,
    setSelectedNode
  } = useAssetsStore();

  const { deleteConnection } = useInternetConnectionStore();
  const { deleteConfig } = useDMZConfigStore();

  const { organization } = useAuth();
  const { risks, deleteRisk, calculateRiskScore } = useRisks(organization?.id);
  const { tasks, deleteTask, completeTask } = useTasks(organization?.id);
  const { evidence, deleteEvidence } = useEvidence(organization?.id);

  const { sites, updateSite } = useSiteStore();
  const { updateZone } = useZoneStore();
  const { systems } = useSystemStore();
  const { cloudServices, updateCloudService } = useCloudServiceStore();

  const [riskDialogOpen, setRiskDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [evidenceDialogOpen, setEvidenceDialogOpen] = useState(false);
  const [nodeEditDialogOpen, setNodeEditDialogOpen] = useState(false);
  const [internetEditDialogOpen, setInternetEditDialogOpen] = useState(false);
  const [dmzEditDialogOpen, setDmzEditDialogOpen] = useState(false);
  const [siteEditDialogOpen, setSiteEditDialogOpen] = useState(false);
  const [zoneEditDialogOpen, setZoneEditDialogOpen] = useState(false);
  const [systemEditDialogOpen, setSystemEditDialogOpen] = useState(false);
  const [cloudServiceEditDialogOpen, setCloudServiceEditDialogOpen] = useState(false);
  const [cloudResourceEditDialogOpen, setCloudResourceEditDialogOpen] = useState(false);
  const [endpointEditDialogOpen, setEndpointEditDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskRecord | null>(null);

  const selectedNode = selectedNodeId ? nodes.find(n => n.id === selectedNodeId) : null;
  
  // Check node types
  const nodeType = selectedNode?.type;
  const isInternetNode = nodeType === 'internet';
  const isDMZNode = nodeType === 'dmz';
  const isSiteNode = nodeType === 'siteGroup';
  const isZoneNode = nodeType === 'zoneGroup';
  const isSystemNode = nodeType === 'systemGroup';
  const isCloudGroupNode = nodeType === 'cloudGroup';
  const isCloudResourceNode = nodeType === 'cloudResource';
  const isEndpointNode = nodeType === 'endpoint';
  
  // Determine entity type for linking based on node type
  const getEntityType = useCallback((): EntityType => {
    if (isInternetNode) return 'internet_connection';
    if (isDMZNode) return 'dmz_config';
    if (isSiteNode) return 'site';
    if (isZoneNode) return 'zone';
    if (isSystemNode) return 'system';
    if (isCloudGroupNode) return 'cloud';
    if (isCloudResourceNode) return 'cloud_resource';
    if (isEndpointNode) return 'endpoint';
    return 'asset';
  }, [isInternetNode, isDMZNode, isSiteNode, isZoneNode, isSystemNode, isCloudGroupNode, isCloudResourceNode, isEndpointNode]);

  // Get the correct entity ID for database operations
  const getEntityId = useCallback((): string => {
    if (!selectedNode) return '';
    if (isInternetNode && selectedNode.data.connectionId) {
      return selectedNode.data.connectionId;
    }
    if (isDMZNode && selectedNode.data.configId) {
      return selectedNode.data.configId;
    }
    return selectedNodeId || '';
  }, [selectedNode, isInternetNode, isDMZNode, selectedNodeId]);

  const entityType = getEntityType();
  const entityId = getEntityId();

  // Use useRelatedEntities hook to get linked entities from the links table
  const relatedEntities = useRelatedEntities(entityType, entityId);

  // Get linked risks, tasks, evidence from the links table
  const nodeRisks = relatedEntities.risks
    .map(r => risks?.find(risk => risk.id === r.id))
    .filter((r): r is NonNullable<typeof r> => r !== undefined);

  const nodeTasks = relatedEntities.tasks
    .map(t => tasks?.find(task => task.id === t.id))
    .filter((t): t is NonNullable<typeof t> => t !== undefined);

  const nodeEvidence = relatedEntities.evidence
    .map(e => evidence?.find(ev => ev.id === e.id))
    .filter((e): e is NonNullable<typeof e> => e !== undefined);
  
  const toTaskRecord = (task: Task): TaskRecord => {
    const cadence = normalizeCadence(task.cadence);
    return {
      id: task.id,
      nodeId: task.related_to_id || entityId || '',
      title: task.title,
      description: task.description || undefined,
      cadence,
      rrule: task.rrule || undefined,
      owner: task.owner || undefined,
      due_next: task.due_next,
      last_run: undefined,
      status: task.status,
      evidence_ids: [],
      tags: [],
    };
  };

  const normalizeCadence = (cadence: string): TaskCadence => {
    if (
      cadence === 'once' ||
      cadence === 'daily' ||
      cadence === 'weekly' ||
      cadence === 'monthly' ||
      cadence === 'quarterly' ||
      cadence === 'yearly' ||
      cadence === 'custom_rrule'
    ) {
      return cadence;
    }
    return 'monthly';
  };

  // Handle Internet node save - update React Flow node data
  const handleInternetSave = useCallback((data: any) => {
    if (onUpdateNodeData && selectedNodeId) {
      onUpdateNodeData(selectedNodeId, {
        label: data.label,
        connectionType: data.connection_type,
        supplier: data.supplier,
        connectionId: data.id,
      });
    }
  }, [onUpdateNodeData, selectedNodeId]);

  // Handle DMZ node save - update React Flow node data
  const handleDMZSave = useCallback((data: any) => {
    if (onUpdateNodeData && selectedNodeId) {
      onUpdateNodeData(selectedNodeId, {
        label: data.label,
        configId: data.id,
        firewallRules: data.rulesCount || 0,
      });
    }
  }, [onUpdateNodeData, selectedNodeId]);

  if (!selectedNode) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-96">
          <SheetHeader>
            <SheetTitle>No Node Selected</SheetTitle>
          </SheetHeader>
          <p className="text-muted-foreground">Select a node to view its details.</p>
        </SheetContent>
      </Sheet>
    );
  }

  const getRiskSeverityColor = (severity: RiskSeverity) => {
    switch (severity) {
      case 'Low':
        return 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20';
      case 'Medium':
        return 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/20';
      case 'High':
        return 'bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-500/20';
      case 'Critical':
        return 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getTaskStatusColor = (status: TaskStatus) => {
    switch (status) {
      case 'Scheduled':
        return 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20';
      case 'Due':
        return 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/20';
      case 'Overdue':
        return 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20';
      case 'Done':
        return 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const handleEditClick = () => {
    if (isInternetNode) {
      setInternetEditDialogOpen(true);
    } else if (isDMZNode) {
      setDmzEditDialogOpen(true);
    } else if (isSiteNode) {
      setSiteEditDialogOpen(true);
    } else if (isZoneNode) {
      setZoneEditDialogOpen(true);
    } else if (isSystemNode) {
      setSystemEditDialogOpen(true);
    } else if (isCloudGroupNode) {
      setCloudServiceEditDialogOpen(true);
    } else if (isCloudResourceNode) {
      setCloudResourceEditDialogOpen(true);
    } else if (isEndpointNode) {
      setEndpointEditDialogOpen(true);
    } else {
      setNodeEditDialogOpen(true);
    }
  };

  const handleDelete = async () => {
    try {
      if (isInternetNode && selectedNode.data.connectionId) {
        await deleteConnection(selectedNode.data.connectionId);
      } else if (isDMZNode && selectedNode.data.configId) {
        await deleteConfig(selectedNode.data.configId);
      }
      await deleteNode(selectedNodeId!);
      setSelectedNode(null);
      onOpenChange(false);
      toast.success('Node deleted successfully');
    } catch (error) {
      toast.error('Failed to delete node');
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-96 overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2">
              {isInternetNode ? (
                <Globe className="h-5 w-5 text-blue-500" />
              ) : isDMZNode ? (
                <Shield className="h-5 w-5 text-orange-500" />
              ) : (
                <Shield className="h-5 w-5" />
              )}
              {selectedNode.data.label}
            </SheetTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleEditClick}
              >
                <Edit className="h-4 w-4" />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Node</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete "{selectedNode.data.label}"? This action cannot be undone and will also remove all associated risks, tasks, and evidence.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete}>
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </SheetHeader>

        <div className="space-y-4 mt-4">
          {/* Node Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Type:</span>
                <span className="text-sm font-medium capitalize">
                  {isInternetNode ? 'Internet Connection' : isDMZNode ? 'DMZ / Firewall' : selectedNode.data.type}
                </span>
              </div>
              {isInternetNode && selectedNode.data.connectionType && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Connection:</span>
                  <Badge variant="outline">{selectedNode.data.connectionType}</Badge>
                </div>
              )}
              {isInternetNode && selectedNode.data.supplier && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">ISP:</span>
                  <span className="text-sm">{selectedNode.data.supplier}</span>
                </div>
              )}
              {isDMZNode && selectedNode.data.firewallRules !== undefined && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Firewall Rules:</span>
                  <Badge variant="outline">{selectedNode.data.firewallRules} rules</Badge>
                </div>
              )}
              {selectedNode.data.domain && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Domain:</span>
                  <Badge variant="outline">{selectedNode.data.domain}</Badge>
                </div>
              )}
              {selectedNode.data.purdue_level && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Purdue Level:</span>
                  <Badge variant="outline">{selectedNode.data.purdue_level}</Badge>
                </div>
              )}
              {selectedNode.data.meta?.ip && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">IP:</span>
                  <span className="text-sm font-mono">{selectedNode.data.meta.ip}</span>
                </div>
              )}
              {selectedNode.data.meta?.model && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Model:</span>
                  <span className="text-sm">{selectedNode.data.meta.model}</span>
                </div>
              )}
              {selectedNode.data.meta?.vendor && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Vendor:</span>
                  <span className="text-sm">{selectedNode.data.meta.vendor}</span>
                </div>
              )}
            </CardContent>
          </Card>

          <Tabs defaultValue="risks" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="risks" className="text-xs">
                Risks ({nodeRisks.length})
              </TabsTrigger>
              <TabsTrigger value="tasks" className="text-xs">
                Tasks ({nodeTasks.length})
              </TabsTrigger>
              <TabsTrigger value="evidence" className="text-xs">
                Evidence ({nodeEvidence.length})
              </TabsTrigger>
              <TabsTrigger value="relationships" className="text-xs">
                Links
              </TabsTrigger>
            </TabsList>

            <TabsContent value="risks" className="space-y-2">
              <div className="flex justify-between items-center">
                <h4 className="font-medium">Risks</h4>
                <Button 
                  size="sm" 
                  onClick={() => setRiskDialogOpen(true)}
                  className="h-7"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add
                </Button>
              </div>
              
              {nodeRisks.length === 0 ? (
                <p className="text-sm text-muted-foreground">No risks identified</p>
              ) : (
                <div className="space-y-2">
                  {nodeRisks.map((risk) => {
                    const { score, severity } = calculateRiskScore(
                      risk.likelihood || 3, 
                      risk.impact || 3
                    );
                    return (
                      <Card key={risk.id} className="p-3">
                        <div className="flex justify-between items-start mb-2">
                          <h5 className="text-sm font-medium">{risk.title}</h5>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteRisk(risk.id)}
                            className="h-6 w-6 p-0"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground mb-2">
                          {risk.cause || risk.consequence}
                        </p>
                        <div className="flex justify-between items-center">
                          <Badge 
                            variant="outline" 
                            className={getRiskSeverityColor(severity as RiskSeverity)}
                          >
                            {severity} ({score})
                          </Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setRiskDialogOpen(true);
                            }}
                            className="h-6 text-xs"
                          >
                            Edit
                          </Button>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="tasks" className="space-y-2">
              <div className="flex justify-between items-center">
                <h4 className="font-medium">Tasks</h4>
                <Button 
                  size="sm" 
                  onClick={() => setTaskDialogOpen(true)}
                  className="h-7"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add
                </Button>
              </div>
              
              {nodeTasks.length === 0 ? (
                <p className="text-sm text-muted-foreground">No tasks scheduled</p>
              ) : (
                <div className="space-y-2">
                  {nodeTasks.map((task) => (
                    <Card key={task.id} className="p-3">
                      <div className="flex justify-between items-start mb-2">
                        <h5 className="text-sm font-medium">{task.title}</h5>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteTask(task.id)}
                          className="h-6 w-6 p-0"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <Badge 
                            variant="outline" 
                            className={getTaskStatusColor(task.status)}
                          >
                            {task.status}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {task.cadence}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Due: {dayjs(task.due_next).fromNow()}
                        </div>
                        <div className="flex gap-1">
                          {task.status !== 'Done' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => completeTask({ id: task.id })}
                              className="h-6 text-xs"
                            >
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Complete
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingTask(toTaskRecord(task));
                              setTaskDialogOpen(true);
                            }}
                            className="h-6 text-xs"
                          >
                            Edit
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="evidence" className="space-y-2">
              <div className="flex justify-between items-center">
                <h4 className="font-medium">Evidence</h4>
                <Button 
                  size="sm" 
                  onClick={() => setEvidenceDialogOpen(true)}
                  className="h-7"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add
                </Button>
              </div>
              
              {nodeEvidence.length === 0 ? (
                <p className="text-sm text-muted-foreground">No evidence attached</p>
              ) : (
                <div className="space-y-2">
                  {nodeEvidence.map((item) => (
                    <Card key={item.id} className="p-3">
                      <div className="flex justify-between items-start mb-2">
                        <h5 className="text-sm font-medium">{item.name}</h5>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteEvidence(item.id)}
                          className="h-6 w-6 p-0"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="space-y-1">
                        <Badge variant="outline">{item.type}</Badge>
                        <div className="text-xs text-muted-foreground">
                          {dayjs(item.created_at).fromNow()}
                        </div>
                        {item.notes && (
                          <p className="text-xs">{item.notes}</p>
                        )}
                        {item.storage_path && (
                          <a 
                            href={item.storage_path} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-primary hover:underline"
                          >
                            Open Link
                          </a>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="relationships" className="space-y-4">
              <LinkManager
                entityType={entityType}
                entityId={entityId}
                entityName={selectedNode.data.label}
                allowedTypes={STANDARD_LINKABLE_TYPES.filter(t => t !== entityType)}
                showHeader={false}
              />
            </TabsContent>
          </Tabs>
        </div>

        {/* Dialogs */}
        <RiskDialog
          open={riskDialogOpen}
          onOpenChange={setRiskDialogOpen}
          nodeId={entityId}
          relatedType={entityType}
        />

        <TaskDialog
          open={taskDialogOpen}
          onOpenChange={(open) => {
            setTaskDialogOpen(open);
            if (!open) setEditingTask(null);
          }}
          nodeId={entityId}
          task={editingTask ?? undefined}
        />

        <EvidenceDialog
          open={evidenceDialogOpen}
          onOpenChange={setEvidenceDialogOpen}
          nodeId={entityId}
          relatedType={entityType}
        />

        <NodeEditDialog
          open={nodeEditDialogOpen}
          onOpenChange={setNodeEditDialogOpen}
          nodeId={selectedNodeId}
        />

        <InternetEditDialog
          open={internetEditDialogOpen}
          onOpenChange={setInternetEditDialogOpen}
          connectionId={isInternetNode ? selectedNode.data.connectionId : undefined}
          initialData={{
            label: selectedNode.data.label,
            connection_type: selectedNode.data.connectionType,
            supplier: selectedNode.data.supplier,
            description: selectedNode.data.description,
            external_link: selectedNode.data.externalLink,
            owner_id: selectedNode.data.ownerId,
            parent_node_id: selectedNode.parentId,
            position_x: selectedNode.position?.x,
            position_y: selectedNode.position?.y,
          }}
          onSave={handleInternetSave}
        />

        <DMZEditDialog
          open={dmzEditDialogOpen}
          onOpenChange={setDmzEditDialogOpen}
          configId={isDMZNode ? selectedNode.data.configId : undefined}
          initialData={{
            label: selectedNode.data.label,
            description: selectedNode.data.description,
            owner_id: selectedNode.data.ownerId,
            parent_node_id: selectedNode.parentId,
            position_x: selectedNode.position?.x,
            position_y: selectedNode.position?.y,
          }}
          onSave={handleDMZSave}
        />

        {/* Site Edit Dialog */}
        {isSiteNode && (
          <SiteFormDialog
            open={siteEditDialogOpen}
            onOpenChange={setSiteEditDialogOpen}
            site={sites.find(s => s.id === selectedNode.data.id)}
            onSubmit={async (siteData) => {
              await updateSite(selectedNode.data.id, siteData);
              setSiteEditDialogOpen(false);
              if (onUpdateNodeData) {
                onUpdateNodeData(selectedNodeId!, { label: siteData.name, ...siteData });
              }
            }}
          />
        )}

        {/* Zone Edit Dialog */}
        {isZoneNode && (
          <ZoneFormDialog
            open={zoneEditDialogOpen}
            onOpenChange={setZoneEditDialogOpen}
            zone={{
              id: selectedNode.data.id,
              name: selectedNode.data.label,
              domain: selectedNode.data.domain,
              purdue_level: selectedNode.data.purdueLevel,
            } as any}
            onSubmit={async (zoneData) => {
              await updateZone(selectedNode.data.id, zoneData);
              setZoneEditDialogOpen(false);
              if (onUpdateNodeData) {
                onUpdateNodeData(selectedNodeId!, { label: zoneData.name, ...zoneData });
              }
            }}
            sites={sites}
          />
        )}

        {/* System Edit Dialog */}
        {isSystemNode && (
          <SystemFormDialog
            open={systemEditDialogOpen}
            onOpenChange={setSystemEditDialogOpen}
            system={systems.find(s => s.id === selectedNode.data.id)}
            zoneId={selectedNode.parentId?.replace('zone-', '') || ''}
          />
        )}

        {/* Cloud Service Edit Dialog */}
        {isCloudGroupNode && selectedNode.data.provider && (
          <CloudServiceFormDialog
            open={cloudServiceEditDialogOpen}
            onOpenChange={setCloudServiceEditDialogOpen}
            cloudService={cloudServices.find(c => c.id === selectedNode.data.id)}
            onSubmit={async (cloudData) => {
              await updateCloudService(selectedNode.data.id, cloudData);
              setCloudServiceEditDialogOpen(false);
              if (onUpdateNodeData) {
                onUpdateNodeData(selectedNodeId!, { label: cloudData.name, ...cloudData });
              }
            }}
          />
        )}

        {/* Cloud Resource Edit Dialog */}
        {isCloudResourceNode && (
          <CloudResourceFormDialog
            open={cloudResourceEditDialogOpen}
            onOpenChange={setCloudResourceEditDialogOpen}
            cloudServiceId={selectedNode.data.cloud_service_id}
            onSuccess={() => setCloudResourceEditDialogOpen(false)}
          />
        )}

        {/* Endpoint Edit Dialog */}
        {isEndpointNode && (
          <EndpointFormDialog
            open={endpointEditDialogOpen}
            onOpenChange={setEndpointEditDialogOpen}
            systemId={selectedNode.data.system_id}
            onSuccess={() => setEndpointEditDialogOpen(false)}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}